import React, { useState, useEffect, PropsWithChildren, ReactElement } from 'react'
import { useDispatch } from 'react-redux'
import { SpreadsheetMapping, SpreadsheetMappingType } from 'api'
import { Locale, Translation } from 'locale'
import { isEmpty, noop, partialRight } from 'lodash'
import { ioService, excelService } from 'services'
import { NotificationActions } from 'store/notification'
import { Nullable } from 'utils'
import { isNotNil } from 'utils/predicate'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Groupbox, ModalContent, ModalFooter, ModalWindow } from 'view/components/panels'
import { NotificationType } from '../modal'

import { ClipboardParseNoRowsFoundError, getDefaultMapping, parseClipboard as defaultParseClipboard, SpreadsheetParseResult } from './model'
import { SpreadsheetParseMappingSelector } from './SpreadsheetParseMappingSelector'
import { UploadDropzoneEmpty, UploadDropzoneFull } from './UploadDropzone'
import { ParseResultPanel } from './ParseResultPanel'
import { SpreadsheetParseContainer, SpreadsheetParseUploadPane } from './SpreadsheetParseDialogStyles'

interface DispatchProps {
  showGrowl: typeof NotificationActions.showGrowl
}

enum DialogState {
  INITIAL,
  CLIPBOARD_DATA_READY,
  FILE_DATA_READY,
  DATA_LOADED,
  DATA_PARSED
}

export type SpreadsheetParseCompleteHandler<R extends SpreadsheetParseResult = SpreadsheetParseResult, D = any> = (completeParams: {
  rowData: D[]
  parseResult: R
}) => void

export type SpreadsheetParseDataHandler<R extends SpreadsheetParseResult = SpreadsheetParseResult, D = any> = (
  rowData: D[],
  filename: string,
  mapping: SpreadsheetMapping
) => Promise<R>

interface Translations {
  resultSectionTitle?: Translation
  emptyDropzoneNarrative: Translation
  parseSuccessMessage: Translation
  parseErrorMessage: Translation
  parseErrorNarrative?: Translation
}

export interface SpreadsheetParseDialogProps<R extends SpreadsheetParseResult, D> {
  canUpload?: () => Nullable<boolean>
  clipboard?: string
  container?: typeof SpreadsheetParseContainer
  parseClipboard?: typeof defaultParseClipboard
  getMappingModal?: (title: string, mapping: SpreadsheetMapping, parseResult: R) => JSX.Element | undefined
  spreadsheetMappingType: SpreadsheetMappingType
  parseResultItemsGetter: (parseResult: R) => any[]
  title: string
  translations: Translations
  uploadFile?: File
  onClose?: () => void
  onComplete: SpreadsheetParseCompleteHandler<R, D>
  onParseData: SpreadsheetParseDataHandler<R, D>
  dataTestId?: string
  canDownloadUploadMapping?: boolean
}

interface ComponentState<R, D> {
  dialogState: DialogState
  selectedSpreadsheetMapping: SpreadsheetMapping
  uploadFile?: File
  clipboard?: string
  rowData?: D[]
  parseResult?: R
}

export type SpreadsheetParseDialogComponentProps<R extends SpreadsheetParseResult, D> = PropsWithChildren<
  SpreadsheetParseDialogProps<R, D>
> &
  DispatchProps

export const SpreadsheetParseDialogComponent = <R extends SpreadsheetParseResult, D>({
  canUpload: _canUpload = () => true,
  children,
  clipboard: _clipboard,
  parseClipboard = defaultParseClipboard,
  container: Container = SpreadsheetParseContainer,
  dataTestId,
  getMappingModal,
  onClose = noop,
  onParseData,
  onComplete,
  parseResultItemsGetter,
  showGrowl,
  spreadsheetMappingType,
  title,
  translations,
  uploadFile: _uploadFile,
  canDownloadUploadMapping = true
}: SpreadsheetParseDialogComponentProps<R, D>): ReactElement<any, any> => {
  const [{ dialogState, selectedSpreadsheetMapping, uploadFile, clipboard, rowData, parseResult }, setState] = useState<
    ComponentState<R, D>
  >({
    dialogState: DialogState.INITIAL,
    selectedSpreadsheetMapping: getDefaultMapping(spreadsheetMappingType),
    uploadFile: _uploadFile,
    clipboard: _clipboard
  })

  const hasParsed = dialogState === DialogState.DATA_PARSED
  const hasData = [DialogState.FILE_DATA_READY, DialogState.CLIPBOARD_DATA_READY].includes(dialogState)

  const canUpload = () => {
    return isNotNil(parseResult) && !isEmpty(parseResultItemsGetter(parseResult)) && isEmpty(parseResult.issues) && _canUpload()
  }

  const resetState = () =>
    setState(s => ({ ...s, rowData: undefined, uploadFile: undefined, clipboard: undefined, dialogState: DialogState.INITIAL }))

  const handleDropFiles = (files: File[]) =>
    setState(s => ({ ...s, uploadFile: files[0], clipboard: undefined, dialogState: DialogState.FILE_DATA_READY }))

  const handlePaste = (c: string) =>
    setState(s => ({ ...s, uploadFile: undefined, clipboard: c, dialogState: DialogState.CLIPBOARD_DATA_READY }))

  useEffect(() => {
    async function getRowDataFromDroppedFileOrClipboard() {
      try {
        setState(s => ({ ...s, rowData: undefined }))

        if (uploadFile) {
          const fileData = await ioService.readFile(uploadFile)
          const data = await excelService.parseXlsx<D>(fileData, { fileType: uploadFile.type })
          setState(s => ({ ...s, rowData: data, dialogState: DialogState.DATA_LOADED }))
        } else if (clipboard) {
          let data: D[] = []
          try {
            data = parseClipboard<D>(clipboard)
          } catch (e) {
            if (!(e instanceof ClipboardParseNoRowsFoundError)) {
              throw e
            }
          }
          setState(s => ({ ...s, rowData: data, dialogState: DialogState.DATA_LOADED }))
        }
      } catch (err) {
        showGrowl({
          type: NotificationType.Error,
          title: Translation.TC_UPLOAD_ERROR_TITLE,
          message: Translation.SC_UPLOAD_ERROR_MESSAGE,
          messageArgs: err
        })
        resetState()
      }
    }

    getRowDataFromDroppedFileOrClipboard()
  }, [uploadFile, clipboard, parseClipboard])

  useEffect(() => {
    async function parseRowDataWithMappings() {
      if (rowData) {
        try {
          const filename = uploadFile?.name ?? Locale.translate(Translation.SC_CLIPBOARD)
          const result = await onParseData(rowData, filename, selectedSpreadsheetMapping)

          setState(s => ({ ...s, parseResult: result, dialogState: DialogState.DATA_PARSED }))
        } catch (e) {
          resetState()
        }
      }
    }

    parseRowDataWithMappings()
  }, [rowData, selectedSpreadsheetMapping])

  return (
    <ModalWindow title={title} shadow={true}>
      <ModalContent>
        <Container data-test-id={dataTestId}>
          <SpreadsheetParseUploadPane>
            <Groupbox title={Locale.translate(Translation.TC_UPLOAD_FILE)}>
              {hasData || hasParsed ? (
                <UploadDropzoneFull
                  uploadFile={uploadFile}
                  clipboard={clipboard}
                  handleDropFiles={handleDropFiles}
                  handlePaste={handlePaste}
                  dataTestId="SpreadsheetParseDropzone"
                />
              ) : (
                <UploadDropzoneEmpty
                  narrative={Locale.translate(translations.emptyDropzoneNarrative)}
                  buttonDataTestId={'SpreadsheetParseDialog-Upload-ButtonBrowse'}
                  handleDropFiles={handleDropFiles}
                  handlePaste={handlePaste}
                  dataTestId="SpreadsheetParseDropzone"
                />
              )}
            </Groupbox>
            {isNotNil(parseResult) ? (
              <Groupbox title={translations.resultSectionTitle ? Locale.translate(translations.resultSectionTitle) : ''}>
                <ParseResultPanel
                  itemField={parseResultItemsGetter(parseResult)}
                  parseResult={parseResult}
                  successMessage={translations.parseSuccessMessage}
                  errorMessage={translations.parseErrorMessage}
                  errorNarrative={translations.parseErrorNarrative}
                />

                {isNotNil(getMappingModal) ? (
                  <SpreadsheetParseMappingSelector
                    selectedSpreadsheetMapping={selectedSpreadsheetMapping}
                    spreadsheetMappingType={spreadsheetMappingType}
                    onSpreadsheetMappingSelected={m => setState(s => ({ ...s, selectedSpreadsheetMapping: m }))}
                    getMappingModal={partialRight(getMappingModal, parseResult)}
                    canDownloadUploadMapping={canDownloadUploadMapping}
                  />
                ) : null}
              </Groupbox>
            ) : null}
          </SpreadsheetParseUploadPane>
          {children}
        </Container>
      </ModalContent>

      <ModalFooter>
        <TextButton dataTestId={'SpreadsheetParseDialog-Upload-ButtonCancel'} type={TextButtonType.Tertiary} onClick={onClose}>
          {Locale.translate(Translation.TC_BUTTON_CANCEL)}
        </TextButton>
        <TextButton
          dataTestId={'SpreadsheetParseDialog-Upload-ButtonComplete'}
          type={TextButtonType.Primary}
          disabled={!canUpload()}
          onClick={() => rowData && parseResult && onComplete({ rowData, parseResult })}>
          {Locale.translate(Translation.TC_BUTTON_UPLOAD)}
        </TextButton>
      </ModalFooter>
    </ModalWindow>
  )
}

export const SpreadsheetParseDialog = <R extends SpreadsheetParseResult, D = any>(
  props: PropsWithChildren<SpreadsheetParseDialogProps<R, D>>
): ReactElement<any, any> => {
  const dispatch = useDispatch()

  return <SpreadsheetParseDialogComponent<R, D> {...props} showGrowl={growl => dispatch(NotificationActions.showGrowl(growl))} />
}
